import React, { useState, useEffect, useContext } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  Key, Plus, Building2, Users, Calendar, DollarSign, 
  Copy, CheckCircle, XCircle, Search, RefreshCw, Eye,
  Shield, Clock, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const PLAN_OPTIONS = [
  { value: 'standard', label: 'Standard', maxUsers: 50, licenseFee: 25000, supportFee: 5000 },
  { value: 'professional', label: 'Professional', maxUsers: 200, licenseFee: 75000, supportFee: 15000 },
  { value: 'enterprise', label: 'Enterprise', maxUsers: -1, licenseFee: 150000, supportFee: 30000 },
];

export const LicenseManagementPage = () => {
  const token = localStorage.getItem('token');
  const [licenses, setLicenses] = useState([]);
  const [stats, setStats] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    plan: 'standard',
    max_users: 50,
    license_fee: 25000,
    support_fee: 5000,
    support_expires_at: '',
    notes: ''
  });

  useEffect(() => {
    loadLicenses();
    loadStats();
  }, []);

  const loadLicenses = async () => {
    try {
      const response = await axios.get(`${API}/admin/licenses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLicenses(response.data.licenses || []);
    } catch (error) {
      console.error('Error loading licenses:', error);
      toast.error('Failed to load licenses');
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/licenses/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handlePlanChange = (plan) => {
    const selectedPlan = PLAN_OPTIONS.find(p => p.value === plan);
    setFormData(prev => ({
      ...prev,
      plan,
      max_users: selectedPlan.maxUsers === -1 ? 999 : selectedPlan.maxUsers,
      license_fee: selectedPlan.licenseFee,
      support_fee: selectedPlan.supportFee
    }));
  };

  const handleGenerateLicense = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/licenses/generate`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('License key generated successfully!');
      setDialogOpen(false);
      setSelectedLicense(response.data.license);
      setViewDialogOpen(true);
      loadLicenses();
      loadStats();
      
      // Reset form
      setFormData({
        company_name: '',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        plan: 'standard',
        max_users: 50,
        license_fee: 25000,
        support_fee: 5000,
        support_expires_at: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate license');
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeLicense = async (licenseId) => {
    if (!window.confirm('Are you sure you want to revoke this license? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/licenses/${licenseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('License revoked successfully');
      loadLicenses();
      loadStats();
      setViewDialogOpen(false);
    } catch (error) {
      toast.error('Failed to revoke license');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const filteredLicenses = licenses.filter(license => 
    license.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    license.license_key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    license.contact_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSupportStatus = (expiresAt) => {
    if (!expiresAt) return { status: 'active', label: 'Active', color: 'emerald' };
    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft < 0) return { status: 'expired', label: 'Expired', color: 'red' };
    if (daysLeft < 30) return { status: 'expiring', label: `${daysLeft} days left`, color: 'amber' };
    return { status: 'active', label: 'Active', color: 'emerald' };
  };

  return (
    <div className="p-8 bg-slate-950 min-h-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-4xl font-bold text-white tracking-tight flex items-center gap-3">
            <Key className="w-10 h-10 text-cyan-400" />
            License Management
          </h1>
          <p className="text-base text-slate-400 mt-2">Generate and manage on-premises license keys</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="generate-license-button"
              className="mt-4 md:mt-0 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-md shadow-sm transition-all active:scale-95"
            >
              <Plus size={18} className="mr-2" />
              Generate License
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
                <Key className="w-6 h-6 text-cyan-400" />
                Generate New License
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Create a license key for on-premises deployment
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleGenerateLicense} className="space-y-4 mt-4">
              {/* Company Info */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-300">Company Name *</Label>
                <Input
                  required
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="Bank ABC Berhad"
                  className="bg-slate-900/50 border-slate-800"
                  data-testid="license-company-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                    placeholder="John Doe"
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">Contact Phone</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_phone: e.target.value }))}
                    placeholder="+60123456789"
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-300">Contact Email</Label>
                <Input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                  placeholder="john@company.com"
                  className="bg-slate-900/50 border-slate-800"
                />
              </div>

              {/* Plan Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-300">Plan *</Label>
                <Select value={formData.plan} onValueChange={handlePlanChange}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map(plan => (
                      <SelectItem key={plan.value} value={plan.value}>
                        {plan.label} - {formatCurrency(plan.licenseFee)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">Max Users</Label>
                  <Input
                    type="number"
                    value={formData.max_users}
                    onChange={(e) => setFormData(prev => ({ ...prev, max_users: parseInt(e.target.value) }))}
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">Support Expires</Label>
                  <Input
                    type="date"
                    value={formData.support_expires_at}
                    onChange={(e) => setFormData(prev => ({ ...prev, support_expires_at: e.target.value }))}
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">License Fee (RM)</Label>
                  <Input
                    type="number"
                    value={formData.license_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, license_fee: parseFloat(e.target.value) }))}
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-300">Annual Support (RM)</Label>
                  <Input
                    type="number"
                    value={formData.support_fee}
                    onChange={(e) => setFormData(prev => ({ ...prev, support_fee: parseFloat(e.target.value) }))}
                    className="bg-slate-900/50 border-slate-800"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-semibold text-slate-300">Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional notes..."
                  className="bg-slate-900/50 border-slate-800 min-h-[80px]"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="generate-license-submit"
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold"
              >
                {loading ? 'Generating...' : 'Generate License Key'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <Key className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total_licenses}</p>
                  <p className="text-xs text-slate-400">Total Licenses</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.activated_licenses}</p>
                  <p className="text-xs text-slate-400">Activated</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.pending_activation}</p>
                  <p className="text-xs text-slate-400">Pending Activation</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{formatCurrency(stats.total_license_revenue)}</p>
                  <p className="text-xs text-slate-400">License Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search Bar */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search by company, license key, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-900/50 border-slate-800"
            data-testid="license-search"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => { loadLicenses(); loadStats(); }}
          className="border-slate-700"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Licenses Table */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Company</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">License Key</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Plan</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Users</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Support</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Status</th>
                  <th className="text-left py-4 px-4 text-slate-400 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLicenses.map((license) => {
                  const supportStatus = getSupportStatus(license.support_expires_at);
                  return (
                    <tr key={license.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="py-4 px-4">
                        <div>
                          <p className="text-white font-medium">{license.company_name}</p>
                          <p className="text-xs text-slate-500">{license.contact_email || 'No email'}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <code className="text-cyan-400 text-sm bg-cyan-500/10 px-2 py-1 rounded">
                            {license.license_key}
                          </code>
                          <button
                            onClick={() => copyToClipboard(license.license_key)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${
                          license.plan === 'enterprise' ? 'bg-amber-500/20 text-amber-400' :
                          license.plan === 'professional' ? 'bg-cyan-500/20 text-cyan-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {license.plan}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-slate-300">
                        {license.max_users === -1 || license.max_users >= 999 ? '∞' : license.max_users}
                      </td>
                      <td className="py-4 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium bg-${supportStatus.color}-500/20 text-${supportStatus.color}-400`}>
                          {supportStatus.label}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        {license.is_active ? (
                          <span className="flex items-center gap-1 text-emerald-400 text-sm">
                            <CheckCircle className="w-4 h-4" />
                            {license.activated_at ? 'Activated' : 'Active'}
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-400 text-sm">
                            <XCircle className="w-4 h-4" />
                            Revoked
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setSelectedLicense(license); setViewDialogOpen(true); }}
                          className="text-slate-400 hover:text-white"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredLicenses.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <Key className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No licenses found</p>
                      <p className="text-sm text-slate-500">Generate your first license key to get started</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* View License Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold flex items-center gap-2">
              <Shield className="w-6 h-6 text-cyan-400" />
              License Details
            </DialogTitle>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4 mt-4">
              {/* License Key Display */}
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs text-cyan-400 mb-1">LICENSE KEY</p>
                <div className="flex items-center justify-between">
                  <code className="text-lg font-mono text-white">{selectedLicense.license_key}</code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedLicense.license_key)}
                    className="text-cyan-400"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Company Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Company</p>
                  <p className="text-white font-medium">{selectedLicense.company_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Plan</p>
                  <p className="text-white font-medium capitalize">{selectedLicense.plan}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Contact Person</p>
                  <p className="text-white">{selectedLicense.contact_person || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Contact Email</p>
                  <p className="text-white">{selectedLicense.contact_email || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Max Users</p>
                  <p className="text-white">{selectedLicense.max_users === -1 ? 'Unlimited' : selectedLicense.max_users}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">License Fee</p>
                  <p className="text-white">{formatCurrency(selectedLicense.license_fee)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Annual Support</p>
                  <p className="text-white">{formatCurrency(selectedLicense.support_fee)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Support Expires</p>
                  <p className="text-white">{selectedLicense.support_expires_at || 'Not set'}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Created</p>
                  <p className="text-white">{new Date(selectedLicense.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Activated</p>
                  <p className="text-white">
                    {selectedLicense.activated_at 
                      ? new Date(selectedLicense.activated_at).toLocaleDateString()
                      : 'Not yet activated'}
                  </p>
                </div>
              </div>

              {selectedLicense.notes && (
                <div>
                  <p className="text-xs text-slate-500">Notes</p>
                  <p className="text-slate-300 text-sm">{selectedLicense.notes}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-slate-800">
                <Button
                  variant="outline"
                  onClick={() => copyToClipboard(
                    `License Key: ${selectedLicense.license_key}\nCompany: ${selectedLicense.company_name}\nPlan: ${selectedLicense.plan}\nMax Users: ${selectedLicense.max_users}`
                  )}
                  className="flex-1 border-slate-700"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Details
                </Button>
                {selectedLicense.is_active && (
                  <Button
                    variant="destructive"
                    onClick={() => handleRevokeLicense(selectedLicense.id)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Revoke
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LicenseManagementPage;
